import React from 'react';
import axios from 'axios';
import { useTokenizedApi } from '@tokenized/sdk-react-private';
import { liquidNobleApiUrl, brandDomain } from '#config/definitions';
import {
  useSelectedProfileId,
  useSelectedEntityId,
} from '#modules/state/ProfileState';
import { ErrorHandlingContext } from '#modules/context/ErrorHandlingContext';
import {
  getAuthToken,
  isLiquidNobleLoggedIn,
  saveAuthToken,
  clearSessionMetadata,
  getSessionProfileId,
  getSessionEntityId,
  getTokenizedAuthToken,
} from '#modules/utils/jwt';

export function addLiquidNobleAuthHeader(config) {
  const authToken = getAuthToken();
  const domain =
    typeof window !== 'undefined' ? window?.location?.hostname : undefined;
  const activeDomain = brandDomain || domain;
  return {
    ...(config ?? {}),
    headers: {
      ...(config?.headers ?? {}),
      ...(activeDomain && { 'x-brand-domain': activeDomain }),
      ...(authToken && { Authorization: `Bearer ${authToken}` }),
    },
  };
}

export function useApiGet(endpoint, config, authTokenSelector) {
  const liquidNobleApi = useLiquidNobleApi();

  const [responseData, setResponseData] = React.useState(null);
  React.useEffect(() => {
    async function initApiRequest() {
      const response = await liquidNobleApi.get(
        endpoint,
        config,
        authTokenSelector,
      );

      const { data } = response;
      if (data) {
        setResponseData(data);
      }
    }
    initApiRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, JSON.stringify(config)]);

  return responseData;
}

const requestErrorHandling = async (requestMethod, setNetworkError) => {
  try {
    const response = await requestMethod();
    setNetworkError(false);
    return response;
  } catch (error) {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx

      // if code 401, clear token
      if (error.response.status === 401 && isLiquidNobleLoggedIn()) {
        saveAuthToken(null);
        clearSessionMetadata();
        window.location.reload();
      }
      throw error.response;
    } else if (error.request) {
      // The request was made but no response was received
      setNetworkError(true);
      throw new Error('#2 Network error');
    } else {
      // Something happened in setting up the request that triggered an Error
      throw error;
    }
  }
};

export default function useLiquidNobleApi() {
  const tokenizedApi = useTokenizedApi();
  const { setNetworkError } = React.useContext(ErrorHandlingContext);
  const liquidNobleApi = React.useMemo(
    () => ({
      get: async (endpoint, config, authTokenSelector) => {
        let authHeader = addLiquidNobleAuthHeader(config);
        try {
          authHeader = await tokenizedApi.endpoints.addAuthHeader(
            config,
            authTokenSelector,
          );
        } catch (e) {
          // console.error('Error adding auth header', e);
        }
        const url = `${liquidNobleApiUrl}${endpoint}`;
        return requestErrorHandling(async () => {
          return await axios.get(url, authHeader);
        }, setNetworkError);
      },
      put: async (endpoint, data, config, authTokenSelector) => {
        let authHeader = addLiquidNobleAuthHeader(config);
        try {
          authHeader = await tokenizedApi.endpoints.addAuthHeader(
            config,
            authTokenSelector,
          );
        } catch (e) {
          // console.error('Error adding auth header', e);
        }
        const url = `${liquidNobleApiUrl}${endpoint}`;
        return requestErrorHandling(async () => {
          return await axios.put(url, data, authHeader);
        }, setNetworkError);
      },
      post: async (endpoint, data, config, authTokenSelector) => {
        let authHeader = addLiquidNobleAuthHeader(config);
        try {
          authHeader = await tokenizedApi.endpoints.addAuthHeader(
            config,
            authTokenSelector,
          );
        } catch (e) {
          // console.error('Error adding auth header', e);
        }
        const url = `${liquidNobleApiUrl}${endpoint}`;
        return requestErrorHandling(async () => {
          return await axios.post(url, data, authHeader);
        }, setNetworkError);
      },
      delete: async (endpoint, config, authTokenSelector) => {
        let authHeader = addLiquidNobleAuthHeader(config);
        try {
          authHeader = await tokenizedApi.endpoints.addAuthHeader(
            config,
            authTokenSelector,
          );
        } catch (e) {
          // console.error('Error adding auth header', e);
        }
        const url = `${liquidNobleApiUrl}${endpoint}`;
        return requestErrorHandling(async () => {
          return await axios.delete(url, authHeader);
        }, setNetworkError);
      },
    }),
    [tokenizedApi.endpoints, setNetworkError],
  );

  return liquidNobleApi;
}

export function useActiveEntityIds() {
  const sessionProfileId = getSessionProfileId();
  const sessionEntityId = getSessionEntityId();
  const profileId = useSelectedProfileId();
  const entityId = useSelectedEntityId();

  if (profileId || entityId || getTokenizedAuthToken()) {
    // Prioritize Tokenized logins based on sessionStorage
    return {
      profileId,
      entityId,
    };
  }

  return {
    profileId: sessionProfileId,
    entityId: sessionEntityId,
  };
}
